import { create } from 'zustand';
import type { Tenant, TenantPlan, PlatformStats } from '@/types';
import * as api from '@/lib/api';

interface PlatformStore {
  tenants: Tenant[];
  plans: TenantPlan[];
  stats: PlatformStats | null;
  selectedTenant: Tenant | null;
  loading: boolean;
  error: string | null;

  loadTenants: () => Promise<void>;
  loadPlans: () => Promise<void>;
  loadStats: () => Promise<void>;
  selectTenant: (id: string) => Promise<void>;
  /** Re-fetch the selected tenant WITHOUT toggling the global loading
   *  flag — used by Dashboard "Refresh metrics" so the page doesn't
   *  unmount into a spinner while we update the storage / user counts. */
  refreshSelectedTenant: (id: string) => Promise<void>;
  clearSelectedTenant: () => void;
  createTenant: (input: Parameters<typeof api.createTenant>[0]) => Promise<Tenant>;
  updateTenant: (id: string, input: Parameters<typeof api.updateTenant>[1]) => Promise<void>;
  suspendTenant: (id: string) => Promise<void>;
  updateBranding: (tenantId: string, input: Parameters<typeof api.updateBranding>[1]) => Promise<void>;
  updateFeatures: (tenantId: string, input: Parameters<typeof api.updateFeatures>[1]) => Promise<void>;
  setError: (e: string | null) => void;
}

export const usePlatformStore = create<PlatformStore>((set, _get) => ({
  tenants: [], plans: [], stats: null, selectedTenant: null, loading: false, error: null,

  loadTenants: async () => {
    set({ loading: true, error: null });
    try { set({ tenants: await api.fetchTenants(), loading: false }); }
    catch (e) { set({ error: (e as Error).message, loading: false }); }
  },

  loadPlans: async () => {
    try { set({ plans: await api.fetchActivePlans() }); }
    catch (e) { set({ error: (e as Error).message }); }
  },

  loadStats: async () => {
    try { set({ stats: await api.fetchPlatformStats() }); }
    catch (e) { set({ error: (e as Error).message }); }
  },

  selectTenant: async (id) => {
    set({ loading: true, error: null });
    try { set({ selectedTenant: await api.fetchTenant(id), loading: false }); }
    catch (e) { set({ error: (e as Error).message, loading: false }); }
  },

  refreshSelectedTenant: async (id) => {
    try { set({ selectedTenant: await api.fetchTenant(id) }); }
    catch (e) { set({ error: (e as Error).message }); }
  },

  clearSelectedTenant: () => set({ selectedTenant: null }),

  createTenant: async (input) => {
    set({ loading: true, error: null });
    try {
      const t = await api.createTenant(input);
      set(s => ({ tenants: [t, ...s.tenants], loading: false }));
      return t;
    } catch (e) { set({ error: (e as Error).message, loading: false }); throw e; }
  },

  updateTenant: async (id, input) => {
    try {
      const updated = await api.updateTenant(id, input);
      set(s => ({
        tenants: s.tenants.map(t => t.id === id ? updated : t),
        selectedTenant: s.selectedTenant?.id === id ? updated : s.selectedTenant,
      }));
    } catch (e) { set({ error: (e as Error).message }); throw e; }
  },

  suspendTenant: async (id) => {
    try {
      await api.suspendTenant(id);
      set(s => ({
        tenants: s.tenants.map(t => t.id === id ? { ...t, status: 'cancelled' as const } : t),
      }));
    } catch (e) { set({ error: (e as Error).message }); throw e; }
  },

  updateBranding: async (tenantId, input) => {
    try {
      const branding = await api.updateBranding(tenantId, input);
      set(s => ({
        selectedTenant: s.selectedTenant?.id === tenantId ? { ...s.selectedTenant, branding } : s.selectedTenant,
        tenants: s.tenants.map(t => t.id === tenantId ? { ...t, branding } : t),
      }));
    } catch (e) { set({ error: (e as Error).message }); throw e; }
  },

  updateFeatures: async (tenantId, input) => {
    try {
      const features = await api.updateFeatures(tenantId, input);
      set(s => ({
        selectedTenant: s.selectedTenant?.id === tenantId ? { ...s.selectedTenant, features } : s.selectedTenant,
        tenants: s.tenants.map(t => t.id === tenantId ? { ...t, features } : t),
      }));
    } catch (e) { set({ error: (e as Error).message }); throw e; }
  },

  setError: (error) => set({ error }),
}));
